import {colors, radii, tokens} from '@commercial-helios/ui/theme'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import PopoverVideo from '@/components/popover-video'
import {PlayBtn} from '@/images/icons/play-btn'
import Button from '@helios/shared/components/button'
import Image from '@helios/shared/components/image'
import type {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import PrismicLink from '@helios/shared/components/prismic-link'
import {Layout, Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type ImageSource from '@helios/shared/types/image'
import mq from '@helios/shared/utils/media-query'

interface Props {
    title: string
    content: string
    imageLeft: ImageSource
    imageRight: ImageSource
    imageButton?: ImageSource
    ctaText?: string
    ctaLink?: PrismicLinkProps
    videoId?: string
    dataTestId?: string
}

export const TwoImageHero = ({
    title,
    content,
    imageLeft,
    imageRight,
    imageButton,
    ctaText,
    ctaLink,
    videoId,
    dataTestId,
}: Props) => (
    <Section padding="small" data-testid={dataTestId}>
        <StyledLayout variant="two-equal">
            <HeadingSection>
                <Text as="h1" variant="heading-large">
                    {title}
                </Text>

                <Image image={imageLeft} />
            </HeadingSection>

            <DescriptionSection>
                <PaddedText variant="title-base" color="muted">
                    {content}
                </PaddedText>

                <CtaArea>
                    <CtaWrapper>
                        {videoId && (
                            <PopoverVideo videoId={videoId}>
                                {({openPopover}) => (
                                    <StyledPlayButton
                                        onClick={openPopover}
                                        variant="outline"
                                        data-testid={`${dataTestId}-cta`}
                                    >
                                        <StyledIcon />
                                        {ctaText}
                                    </StyledPlayButton>
                                )}
                            </PopoverVideo>
                        )}
                        {!videoId && ctaLink && (
                            <StyledButton
                                data-testid={`${dataTestId}-cta`}
                                forwardedAs={PrismicLink}
                                {...ctaLink}
                            >
                                {ctaText}
                            </StyledButton>
                        )}
                    </CtaWrapper>
                    {imageButton && <ImageBelowCta image={imageButton} />}
                </CtaArea>

                <StyledImage image={imageRight} />
            </DescriptionSection>
        </StyledLayout>
    </Section>
)

const MOBILE_BREAKPOINT = 'xs'
const TABLET_BREAKPOINT = 'sm'

const StyledLayout = styled(Layout)`
    ${mq.min(MOBILE_BREAKPOINT)} {
        grid-template-columns: 1fr 1fr;
        gap: 0;
    }
`

const ImageBelowCta = styled(Image)`
    ${mq.max(MOBILE_BREAKPOINT)} {
        justify-self: end;
    }
`

const HeadingSection = styled.div`
    display: grid;
    grid-row-gap: ${tokens.spacing12};

    ${mq.min(MOBILE_BREAKPOINT)} {
        grid-row-gap: ${tokens.spacing40};
    }

    ${mq.min(TABLET_BREAKPOINT)} {
        grid-row-gap: 80px;
    }
`

const DescriptionSection = styled.div`
    display: grid;
    grid-row-gap: ${tokens.spacing20};

    ${mq.min(TABLET_BREAKPOINT)} {
        grid-row-gap: ${tokens.spacing40};
    }
`

const PaddedText = styled(Text)`
    ${mq.min(MOBILE_BREAKPOINT)} {
        padding-right: ${tokens.spacing40};
        padding-left: ${tokens.spacing40};
    }
`

const CtaArea = styled.div`
    display: grid;
`

const CtaWrapper = styled.div`
    display: grid;

    ${mq.min(MOBILE_BREAKPOINT)} {
        padding-right: ${tokens.spacing40};
        padding-left: ${tokens.spacing40};
    }
`

const StyledButton = styled(Button)`
    ${mq.min(MOBILE_BREAKPOINT)} {
        justify-self: left;
    }
`

const StyledPlayButton = styled(Button)`
    padding: ${tokens.spacing14};
    font-size: ${tokens.fontLarge};

    span {
        display: grid;
        align-items: center;
        grid-auto-flow: column;
        grid-column-gap: ${tokens.spacing10};
    }

    :hover {
        svg {
            background-color: ${colors.button.outline.hover.foreground};

            g path {
                fill: ${colors.button.outline.foreground};
            }
        }
    }

    ${mq.min(MOBILE_BREAKPOINT)} {
        justify-self: left;
    }
`

const StyledIcon = styled(PlayBtn)`
    width: 24px;
    height: 24px;
    padding: ${tokens.spacing6};
    overflow: inherit;
    background-color: ${colors.button.outline.foreground};
    border-radius: ${radii.circle};

    ${mq.min(MOBILE_BREAKPOINT)} {
        width: 32px;
        height: 32px;
        padding: ${tokens.spacing8};
    }
`

const StyledImage = styled(Image)`
    ${mq.min(MOBILE_BREAKPOINT)} {
        grid-row-start: 1;
    }
`

export const twoImageHeroFragment = graphql`
    fragment TwoImageHero on PrismicSlicePageDataBodyTwoImageHero {
        __typename
        primary {
            title
            content
            cta_text
            cta_link {
                ...PrismicLink
            }
            video_id
            image_left {
                dimensions {
                    height
                    width
                }
                alt
                copyright
                url
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumns)
            }
            image_right {
                dimensions {
                    height
                    width
                }
                alt
                copyright
                url
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumns)
            }
            image_button {
                dimensions {
                    height
                    width
                }
                alt
                copyright
                url
                gatsbyImageData(
                    breakpoints: [60, 80, 120, 180]
                    sizes: "(max-width: 767px) 40px, 60px"
                )
            }
        }
    }
`
